import {IconExternalLink, IconQrcode} from "@tabler/icons-react";
import PropTypes from "prop-types";
import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {PrimaryButton, SecondaryButton} from "@/components/Button";
import {useBinanceTransactionStatus, usePostBinanceDeposit} from "../../../hooks/wallet/usePostBinanceDeposit";
import styles from "./BinanceOptions.module.css";

const BinanceQRModal = ({visible, onClose, qrUrl, paymentUrl, orderId, amount}) => {
  const {data: transactionStatusResponse, isLoading} = useBinanceTransactionStatus(orderId, visible);
  const {t} = useTranslation();
  const [feedback, setFeedback] = useState(null);

  const transactionData = transactionStatusResponse?.data?.data;
  const actualStatus = transactionData?.status;

  useEffect(() => {
    if (actualStatus === "completed" || actualStatus === "success") {
      setFeedback({type: "success", message: t("depositInstructionsBinancePaymentCompleted")});
      setTimeout(() => onClose(), 5000);
    }
  }, [actualStatus, onClose, t]);

  if (!visible) return null;

  const handleOpenLink = () => {
    window.open(paymentUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
    >
      <div
        className={styles.modal}
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={onClose}
        >
          ✕
        </button>

        {/* ── Hero Section ── */}
        <div className={styles.heroSection}>
          <h3 className={styles.modalTitle}>{t("depositInstructionsBinanceCompletePayment")}</h3>
          <span className={styles.amountBadge}>{amount} USDT</span>
        </div>

        {/* ── Subtitle ── */}
        <p className={styles.subtitle}>{t("depositInstructionsBinanceOptions")}</p>

        {/* ── Payment Method Cards ── */}
        <div className={styles.methodsGrid}>
          {qrUrl && (
            <div className={styles.methodCard}>
              <div className={styles.methodCardHeader}>
                <span className={styles.methodCardIcon}>
                  <IconQrcode size={20} />
                </span>
                <h4 className={styles.methodCardTitle}>{t("depositInstructionsBinanceScanQR")}</h4>
              </div>
              <p className={styles.methodCardHint}>{t("depositInstructionsBinanceUseQR")}</p>
              <img
                src={qrUrl}
                alt="Payment QR Code"
                className={styles.qrImage}
              />
            </div>
          )}

          {qrUrl && paymentUrl && (
            <div className={styles.orDivider}>
              <span className={styles.orLine} />
              <span className={styles.orText}>{t("depositInstructionsBinanceOrSeparator")}</span>
              <span className={styles.orLine} />
            </div>
          )}

          {paymentUrl && (
            <div className={styles.methodCard}>
              <div className={styles.methodCardHeader}>
                <span className={styles.methodCardIcon}>
                  <IconExternalLink size={20} />
                </span>
                <h4 className={styles.methodCardTitle}>{t("depositInstructionsBinancePaymentLinkTitle")}</h4>
              </div>
              <p className={styles.methodCardHint}>{t("depositInstructionsBinanceLink")}</p>
              <button
                type="button"
                className={styles.paymentLink}
                onClick={handleOpenLink}
              >
                <IconExternalLink size={18} />
                {t("depositInstructionsBinanceOpenLink")}
              </button>
            </div>
          )}
        </div>

        {/* ── Feedback ── */}
        {feedback && <div className={`${styles.feedback} ${styles[feedback.type]}`}>{feedback.message}</div>}

        {/* ── Status ── */}
        <div className={styles.statusPill}>
          <span className={styles.statusDot} />
          {t("depositInstructionsBinanceStatus")}:{" "}
          {isLoading
            ? t("depositInstructionsBinanceCheckingStatus")
            : actualStatus || t("depositInstructionsBinancePending")}
        </div>
      </div>
    </div>
  );
};

const BinanceOptions = ({prev}) => {
  const [amount, setAmount] = useState("");
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [depositData, setDepositData] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const binanceDepositMutation = usePostBinanceDeposit();
  const {t} = useTranslation();

  const handleAmountChange = e => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const handleBack = () => {
    setAmount("");
    prev?.();
  };

  const handleContinue = async () => {
    if (!amount || Number.parseFloat(amount) < 10) {
      setFeedback({type: "error", message: t("depositInstructionsBinanceValidAmount")});
      setTimeout(() => setFeedback(null), 4000);
      return;
    }

    try {
      const response = await binanceDepositMutation.mutateAsync(amount);

      if (response?.data?.status === 1) {
        setDepositData(response.data.data);
        setQrModalVisible(true);
        setFeedback(null);
      } else {
        setFeedback({type: "error", message: response?.data?.message || t("depositInstructionsBinanceError")});
        setTimeout(() => setFeedback(null), 4000);
      }
    } catch {
      setFeedback({type: "error", message: t("depositInstructionsBinanceError")});
      setTimeout(() => setFeedback(null), 4000);
    }
  };

  const handleCloseQRModal = () => {
    setQrModalVisible(false);
    setDepositData(null);
    setAmount("");
  };

  return (
    <div className={styles.container}>
      <h4 className={styles.heading}>{t("depositInstructionsBinanceTitle")}</h4>

      <div className={styles.fieldGroup}>
        <label className={styles.fieldLabel}>{t("depositInstructionsBinanceAmount")}</label>
        <input
          type="text"
          inputMode="decimal"
          className={styles.input}
          value={amount}
          onChange={handleAmountChange}
          placeholder={t("depositInstructionsBinanceEnterAmount")}
        />
        <p className={styles.fieldHint}>{t("depositInstructionsBinanceMinAmount")}: $10.00 USDT</p>
      </div>

      {feedback && <div className={`${styles.feedback} ${styles[feedback.type]}`}>{feedback.message}</div>}

      <div className={styles.actionsBar}>
        <PrimaryButton
          onClick={handleContinue}
          disabled={!amount || Number.parseFloat(amount) < 10 || binanceDepositMutation.isPending}
          fullWidth
        >
          {binanceDepositMutation.isPending ? "..." : t("depositInstructionsBinanceContinue")}
        </PrimaryButton>
        <div className={styles.secondaryRow}>
          <SecondaryButton onClick={handleBack}>{t("depositInstructionsBinanceBack")}</SecondaryButton>
        </div>
      </div>

      <BinanceQRModal
        visible={qrModalVisible}
        onClose={handleCloseQRModal}
        qrUrl={depositData?.payment_qr}
        paymentUrl={depositData?.payment_url}
        orderId={depositData?.order_id}
        amount={amount}
      />
    </div>
  );
};

BinanceOptions.propTypes = {
  prev: PropTypes.func,
};

BinanceQRModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  qrUrl: PropTypes.string,
  paymentUrl: PropTypes.string,
  orderId: PropTypes.string,
  amount: PropTypes.string,
};

export default BinanceOptions;
